import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import HidePasswordSvg from '@/components/common/HidePasswordSvg';
import LoadingSvg from '@/components/common/LoadingSvg';
import ViewPasswordSvg from '@/components/common/ViewPasswordSvg';
import { Cookie } from '@/tscapi/utils/cookie';

import { useSignin } from './hooks/useSignin';
import { SigninSchema, SigninFormType } from './validation';

type SigninFormProps = {
    isCheckout: boolean;
    title: string;
    executeRecaptcha: () => Promise<string>;
};

export const SigninForm = ({ isCheckout, title, executeRecaptcha }: SigninFormProps) => {
    const [globalError, setGlobalError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [signoutMessage, setSignoutMessage] = useState<string | null>(null);

    useEffect(() => {
        const cookie = new Cookie();
        const message = cookie.SignOutMessage;
        if (message) {
            setSignoutMessage(message);
        }
    }, []);

    const signoutMessaged = signoutMessage != undefined && signoutMessage.trim().length > 2;

    const {
        register,
        handleSubmit,
        watch,
        clearErrors,
        formState: { errors, isSubmitting },
    } = useForm<SigninFormType>({
        resolver: zodResolver(SigninSchema),
        mode: 'onSubmit',
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const mutation = useSignin();

    const toggleShowPassword = () => setShowPassword((prevState) => !prevState);

    const handleGuestCheckout = () => {
        const cookie = new Cookie();
        const targetUrl = cookie.TargetUrl;

        if (targetUrl && targetUrl === '/pages/expresscheckout') {
            cookie.ExpressCheckoutCreateAccount = 'Y';
            window.location.href = '/pages/expresscheckout';
        }
    };

    const onSubmit = async (data: SigninFormType) => {
        setGlobalError('');

        try {
            const recaptchaToken = await executeRecaptcha();

            mutation.mutate(
                { ...data, recaptchaToken },
                {
                    onError: (error: any) => {
                        setGlobalError(error.message || 'Sign in failed');
                    },
                    onSuccess: () => {
                        console.log('Login successful');

                        if (isCheckout) {
                            window.location.href = '/pages/expresscheckout';
                        } else {
                            const cookie = new Cookie();
                            const targetUrl = cookie.TargetUrl;

                            if (targetUrl && targetUrl !== '/pages/signin') {
                                window.location.href = targetUrl;
                            } else {
                                window.location.href = '/pages/myaccount';
                            }
                        }
                    },
                },
            );
        } catch (error) {
            setGlobalError('reCAPTCHA verification failed. Please try again.');
        }
    };

    return (
        <div className="signin__form-container">
            <h1 className="m-0 mb-5 pt-5 text-center text-2xl font-bold leading-8 text-[#1a1a1a]">{title}</h1>

            {/* Extra buttons for checkout context */}
            {isCheckout && (
                <div>
                    <div className="mt-12 border border-solid border-black">
                        <button
                            type="button"
                            className="w-full border border-black px-6 py-3 font-semibold text-black hover:bg-gray-100"
                            onClick={handleGuestCheckout}
                        >
                            Continue as a Guest
                        </button>
                    </div>
                    <div className="mb-4 mt-8 flex items-center">
                        <div className="h-px flex-1 bg-gray-300"></div>
                        <div className="px-3">Or</div>
                        <div className="h-px flex-1 bg-gray-300"></div>
                    </div>
                </div>
            )}

            {/* Signout Message */}
            {signoutMessaged && (
                <div className="border-2 border-solid border-green-800 font-semibold">
                    <span className="flex items-center">
                        <svg className="mx-2 my-3 h-6 w-6">
                            <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="/dist/svg.svg#check-circle" />
                        </svg>
                        You&apos;ve been signed out successfully
                    </span>
                </div>
            )}

            {globalError && (
                <div className="notification__red relative mb-7 mt-4 flex items-start border-2 border-solid border-red-600 py-3 pl-2 pr-7 text-sm">
                    <img
                        src="/App_Assets/public/icon-exclamation-black.svg"
                        alt="exclamation-icon"
                        className="exclamation-img mr-2 max-w-[20px]"
                    />
                    <div>
                        <strong>{globalError}</strong>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col" noValidate>
                <div className="mb-2">
                    {/* Email Field */}
                    <div className="relative z-0 mb-6 mt-3 flex flex-col justify-center">
                        <input
                            className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            type="email"
                            placeholder=" "
                            id="email"
                            maxLength={50}
                            autoComplete="email"
                            tabIndex={0}
                            data-error={errors.email ? 'true' : 'false'}
                            onChange={() => {
                                if (errors.email) {
                                    clearErrors('email');
                                }
                            }}
                            {...register('email')}
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-sm font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-autofill:-translate-y-5 peer-autofill:scale-75 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75 peer-[-webkit-autofill]:-translate-y-5 peer-[-webkit-autofill]:scale-75"
                            htmlFor="email"
                        >
                            Email address
                        </label>
                        {errors.email && <div className="mt-1 text-xs text-[#df0c0c]">{errors.email.message}</div>}
                    </div>

                    {/* Password Field */}
                    <div className="relative z-0 mb-6 flex flex-col justify-center">
                        <input
                            className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent py-1 pl-0 pr-8 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            type={showPassword ? 'text' : 'password'}
                            placeholder=" "
                            maxLength={16}
                            id="password"
                            autoComplete="current-password"
                            tabIndex={0}
                            data-error={errors.password ? 'true' : 'false'}
                            onChange={() => {
                                if (errors.password) {
                                    clearErrors('password');
                                }
                            }}
                            {...register('password')}
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-sm font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-autofill:-translate-y-5 peer-autofill:scale-75 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75 peer-[-webkit-autofill]:-translate-y-5 peer-[-webkit-autofill]:scale-75"
                            htmlFor="password"
                        >
                            Password
                        </label>
                        <button
                            className="absolute right-1.5 top-[18px] z-[2]"
                            type="button"
                            onClick={toggleShowPassword}
                            tabIndex={0}
                        >
                            {showPassword ? (
                                <ViewPasswordSvg width="20" height="19" />
                            ) : (
                                <HidePasswordSvg width="20" height="20" />
                            )}
                        </button>
                        {errors.password && (
                            <div className="mt-1 text-xs text-[#df0c0c]">{errors.password.message}</div>
                        )}
                    </div>
                </div>

                <div className="text-center">
                    <a href="/pages/passwordrequest" className="text-sm underline hover:font-semibold hover:underline">
                        Forgot password?
                    </a>
                </div>

                {/* Submit Button */}
                <div className={`mt-7 ${isCheckout ? 'mb-5' : ''}`}>
                    <button
                        type="submit"
                        className="flex w-full items-center justify-center bg-black px-6 py-3 font-semibold text-white disabled:bg-black disabled:font-normal"
                        style={{ height: '44px' }}
                        disabled={isSubmitting || mutation.isLoading}
                        tabIndex={0}
                    >
                        {mutation.isLoading && <LoadingSvg width="20" height="20" className="invert" />}
                        <span className={mutation.isLoading ? 'ml-2' : ''}>Sign In</span>
                    </button>
                </div>

                {!isCheckout && (
                    <>
                        <span className="mb-4 mt-16 text-center text-2xl font-bold leading-8 text-[#1a1a1a]">
                            Don&apos;t have an account?{' '}
                        </span>
                        <a
                            href="/pages/createaccount"
                            className="mb-6 block border border-solid border-black p-3 text-center text-sm font-semibold hover:cursor-pointer"
                        >
                            Create an account
                        </a>
                        <a
                            href="/pages/createphoneaccount"
                            className="mb-5 block border border-solid border-black p-3 text-center text-sm font-semibold hover:cursor-pointer"
                        >
                            Transfer phone account
                        </a>
                    </>
                )}
            </form>
        </div>
    );
};
